// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { inject, Injectable } from '@angular/core';
import { MapApiService } from '../../shared/services/map-api.service';
import { getHighestConsumedParts } from '../../../api/remote-api/.spareparts.generated/fn/spare-parts/get-highest-consumed-parts';
import { getAmountConsumedParts } from '../../../api/remote-api/.spareparts.generated/fn/spare-parts/get-amount-consumed-parts';
import * as echarts from 'echarts';

@Injectable({
  providedIn: 'root',
})
export class ChartsService {
  private readonly mapApiService = inject(MapApiService);
  private readonly siemensSans = 'siemens sans';

  public getHighestConsumedParts = this.mapApiService.mapEndpoint(
    getHighestConsumedParts,
  );

  public getAmountConsumedParts = this.mapApiService.mapEndpoint(
    getAmountConsumedParts,
  );

  public buildChart(
    chartEl: HTMLElement,
    option: echarts.EChartsOption,
  ): echarts.ECharts | null {
    if (!chartEl) {
      return null;
    }
    const chartRef = echarts.init(chartEl, null, {
      width: 'auto',
      height: 'auto',
    });
    new ResizeObserver(() => chartRef.resize()).observe(chartEl);
    chartRef.setOption(option);
    return chartRef;
  }

  buildTitle(title: string, subtitle: string): echarts.TitleComponentOption[] {
    return [
      {
        text: title,
        left: 0,
        top: 'top',
        textStyle: {
          color: 'rgba(0, 0, 0, 0.90)',
          fontSize: 14,
          fontWeight: 700,
          fontFamily: this.siemensSans,
        },
      },
      {
        text: subtitle,
        right: 0,
        top: 'top',
        textStyle: {
          fontWeight: 'lighter',
          fontSize: 14,
          fontFamily: this.siemensSans,
        },
      },
    ];
  }

  public buildDefaultOptions(
    title: echarts.EChartsOption['title'],
    xAxis: echarts.EChartsOption['xAxis'],
    yAxis: echarts.EChartsOption['yAxis'],
    series: echarts.EChartsOption['series'],
    grid: echarts.EChartsOption['grid'] = {},
  ): echarts.EChartsOption {
    return { title, xAxis, yAxis, series, grid };
  }

  public getAmountConsumedPartsData$ =
    this.getAmountConsumedParts.lastSuccessfulData$;

  public getHighestConsumedPartsData$ =
    this.getHighestConsumedParts.lastSuccessfulData$;
}
